import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from "react-router";
import Countdown, { zeroPad } from 'react-countdown';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import ReactCodeInput from "react-code-input";
import { Location } from 'history';
import client from 'lib/CommonApi/client';
import { callAlertWeb, callNativeWeb, footerShowEventWeb } from 'common/appInterface';
import stringUtils from 'common/stringUtils';
import { isNullOrBlank } from 'common/utils';
import { useLoading } from 'components/Loading/LoadingProvider';

const MobileVerifyConfirmPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const { showLoading, hideLoading } = useLoading();

  /** Code입력 Ref */
  const inputRef = useRef<HTMLDivElement>(null);

  const screenId = "APPL07";
  const screenName = t("APPL07.title");

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep3>>(location.state.apply);
  const count = useRef(Date.now() + (6 * 60 * 1000));
  const [focus, setFocus] = useState(false);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 유효시간 종료처리 */
  const callbackCountdownComplet = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 인증번호 확인 */
  const clickConfirm = useCallback(() => {
    showLoading();
    client
      .patch('/api/app/apply/verify/confirm', {
        ...location.state.query,
        receiveSeq: cardApply.receiveSeq,
        receiveDate: cardApply.receiveDate,
        certificationProcessId: cardApply.certificationProcessId,
        certificationToken: cardApply.certificationToken
      })
      .then((response) => {
        let result = response.data.response;

        // 정상 or KCB CI불일치
        if (
          response.data.code === '00' ||
          (response.data.code === '05' && result.kcbCertificationYn === 'Y')
        ) {
          // 카드신청내역 등록
          client
            .patch('/api/app/apply/history', {
              ...location.state.query,
              receiveSeq: cardApply.receiveSeq,
              receiveDate: cardApply.receiveDate,
              receiveStatusCode:
                result.kcbCertificationYn === 'Y' ? '03' : '04',
              phoneNo: cardApply.phoneNo,
              cardProductCode: cardApply.cardProductCode
            })
            .then(() => {
              /**
               * mhkang
               * KYC 승인날짜 확인 후 step4(신분증업로드) 또는 step5(회원정보입력) 화면으로 이동
               */
              client
                .get('/api/app/apply/info/approval/date', {
                  params: location.state.query
                })
                .then((response) => {
                  if (response.data.code === '00') {
                    var betweenDays = 1000;
                    let approvalDate = response.data.response.kycApprovalDate;

                    if (!isNullOrBlank(approvalDate)) {
                      console.log('ApprovalDate=' + approvalDate);
                      betweenDays = stringUtils.betweenDays(
                        response.data.response.kycApprovalDate
                      );
                      console.log('BetweenDays=' + betweenDays);
                    }

                    if (betweenDays > 180)
                      navigate('/apply/step4', {
                        state: { ...location.state, apply: { ...cardApply } }
                      });
                    else
                      navigate('/apply/step5', {
                        state: { ...location.state, apply: cardApply }
                      });
                  } else {
                    // 실패값 응답시에는 step4(신분증업로드) 화면으로 이동
                    navigate('/apply/step4', {
                      state: { ...location.state, apply: { ...cardApply } }
                    });
                  }
                })
                .catch((e: AxiosError) => {
                  // 오류 발생시 step4(신분증업로드) 화면으로 이동
                  navigate('/apply/step4', {
                    state: { ...location.state, apply: { ...cardApply } }
                  });
                })
                .finally(() => {
                  hideLoading();
                });
            })
            .catch((e: AxiosError) => {
              callAlertWeb('ERROR', '99', e.message);
            })
            .finally(() => {
              hideLoading();
            });
        }
        // OTP 불일치
        else if (response.data.code === '02') {
          hideLoading();
          callAlertWeb('ERROR', response.data.code, response.data.message);
          setCardApply((value) => ({ ...value, certificationToken: '' }));
          setFocus(false);
          setFocus(true);
        } else {
          hideLoading();
          callAlertWeb('ERROR', response.data.code, response.data.message);
          setCardApply((value) => ({ ...value, certificationToken: '' }));
          setFocus(false);
          setFocus(true);
        }
      })
      .catch((e: AxiosError) => {
        hideLoading();
        callAlertWeb('ERROR', '99', e.message);
      });
  }, [location, navigate, cardApply, showLoading, hideLoading]);

  // 6자리 입력시 자동 확인처리
  useEffect(() => {
    if (cardApply.certificationToken?.length === 6) {
      clickConfirm();
    }
  }, [cardApply.certificationToken, clickConfirm]);

  /** Code입력칸 중간에 클릭되어도 마지막 칸으로 포커스 가도록 처리 */
  const clickPinInput = useCallback((event) => {
    event.stopPropagation();

    if (inputRef?.current) {
      for (let node of inputRef.current.childNodes[0].childNodes as NodeListOf<HTMLInputElement>) {
        if (!node.value) {
          node.focus();
          break;
        }
      }
    }
  }, []);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);
    console.log(cardApply);

    // 포커스 설정
    // setTimeout(() => inputRef.current?.setState({ autoFocus: true }), 500);
    setTimeout(() => setFocus(true), 200);

    // BODY클래스 추가
    document.body.classList.add("bgColorTy02");

    return () => {
      // BODY클래스 제거
      document.body.classList.remove("bgColorTy02");
    }

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents pb64-mb">
          <div className="singUpWrapSub">
            <div className="txtWrap mgt10">
              <p className="mainTxt2">{t("APPL07.explan01")}</p>
              <p className="phnum">{stringUtils.hpFormat(cardApply.phoneNo)}</p>
              <p className="change"><span onClick={() => navigate(-1)} style={{ color: "#e86c43", textDecoration: "underline", textUnderlinePosition: "under" }}>{t("APPL07.explan02")}</span></p>
              <p className="time">
                {/* 인증번호 입력 카운트다운 */}
                <Countdown date={count.current} renderer={({ minutes, seconds }) => (<span>{zeroPad(minutes)}:{zeroPad(seconds)}</span>)} onComplete={callbackCountdownComplet} />
              </p>
            </div>
            <div className="passwordWrap" ref={inputRef} onClick={clickPinInput}>
              {focus && <ReactCodeInput type="number" fields={6} inputMode="numeric" name="certificationToken" onChange={(value) => setCardApply({ ...cardApply, certificationToken: value })} inputStyle={{ width: "32px" }} />}
              {/*<input placeholder=" " type="number" value={stringUtils.nvlDefault(cardApply.certificationToken)} onChange={(e) => setCardApply({ ...cardApply, certificationToken: e.target.value.slice(0, e.target.maxLength) })} style={{ textAlign: "center" }} maxLength={6} inputMode="decimal" ref={inputRef} />*/}
            </div>
            <div className="btnWrapTy01">
              {/*<button type="button" className="btnPrimaryTy01" disabled={stringUtils.checkValueNull(cardApply.certificationToken) || cardApply.certificationToken!.length < 6} onClick={clickConfirm}><span>{t("APPL07.button01")}</span></button>*/}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MobileVerifyConfirmPage;